import React, { useState, useEffect } from "react";
import DynamicTable from "@atlaskit/dynamic-table";
import { useDispatch, useSelector } from "react-redux";
import uuid from "react-uuid";
import AddIcon from "@atlaskit/icon/core/add";
import DeleteIcon from "@atlaskit/icon/core/delete";
import Button from "@atlaskit/button/new";
import { setEditorSaved, selectFlow } from "../../../../redux/actions/integrationActions";
import CellTextField from "../../../../common/components/CellTextField";
import { getAllActionFlowVariables } from "./getAllActionFlowVariables";
import { Collapse } from "antd";
import ArrowUpIcon from "@atlaskit/icon/core/arrow-up";
import ArrowDownIcon from "@atlaskit/icon/core/arrow-down";

export const staticVariableHeaders = {
  cells: [
    {
      content: "Name", // New "Name" column
      key: "name",
      isSortable: false,
      width: 30,
    },
    {
      content: "Value", // "Value" column
      key: "value",
      isSortable: false,
      width: 55,
    },
    {
      content: "Action", // "Action" column
      key: "action",
      isSortable: false,
      width: 15,
    },
  ],
};

const SharedFlowVariablesTable = () => {
  const flow = useSelector((state) => state.integration.flow);
  const dispatch = useDispatch();
  const [localVariables, setLocalVariables] = useState([]);
  const [actionFlowVars, setActionFlowVars] = useState(undefined);
  const saved = useSelector((state) => state.integration.saved);

  const { Panel } = Collapse;

  let timeout = null;

  useEffect(() => {
    //only do this on initial load
    if (localVariables.length === 0) {
      if (flow && flow.flowVariables) {
        //console.log("updated local based on flow", flow);
        setLocalVariables(flow.flowVariables.map((variable) => ({ ...variable }))); // Add unique id to each variable
      } else {
        setLocalVariables([]);
      }
    }

    if (flow) {
      setActionFlowVars(getAllActionFlowVariables(flow));
    }
  }, [flow]);

  useEffect(() => {
    if (localVariables.length > 0) {
      dispatch(selectFlow({ ...flow, flowVariables: localVariables }));
    }
  }, [localVariables]);

  const onRemoveHeader = (index) => {
    const updatedVariables = [...localVariables];
    updatedVariables.splice(index, 1);
    setLocalVariables(updatedVariables);
    if (saved) {
      dispatch(setEditorSaved(false)); // Trigger save state change
    }
  };

  const onChangeFieldValue = (value, index, type) => {
    clearTimeout(timeout);

    const updatedVariables = [...localVariables];
    if (type === "NAME") {
      updatedVariables[index].name = value;
    } else if (type === "VALUE") {
      updatedVariables[index].value = value;
    }
    timeout = setTimeout(function () {
      setLocalVariables(updatedVariables); // Only update local variables without triggering full re-render
    }, 500);

    if (saved) {
      dispatch(setEditorSaved(false));
    }
  };

  const moveVariable = (fromIndex, toIndex) => {
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= localVariables.length || toIndex >= localVariables.length) {
      return;
    }

    const updatedVariables = [...localVariables];
    const [movedItem] = updatedVariables.splice(fromIndex, 1);
    updatedVariables.splice(toIndex, 0, movedItem);
    setLocalVariables(updatedVariables);

    if (saved) {
      dispatch(setEditorSaved(false));
    }
  };

  const getButton = (index) => (
    <div style={{ display: "flex", gap: "4px" }}>
      <div>
        <Button onClick={() => onRemoveHeader(index)} iconBefore={DeleteIcon} aria-label="Remove Header" />
      </div>
      {index > 0 && (
        <div>
          <Button onClick={() => moveVariable(index, index - 1)} iconBefore={ArrowUpIcon} />
        </div>
      )}
      {index < localVariables.length - 1 && (
        <div>
          <Button onClick={() => moveVariable(index, index + 1)} iconBefore={ArrowDownIcon} />
        </div>
      )}
    </div>
  );

  const createRow = (item, index) => ({
    key: item.id, // Use unique id to prevent unnecessary re-renders
    cells: [
      {
        content: <CellTextField inputValue={item.name || ""} index={index} onChange={onChangeFieldValue} type="NAME" />,
        key: `${item.id}-name`,
      },
      {
        content: <CellTextField inputValue={item.value || ""} index={index} onChange={onChangeFieldValue} type="VALUE" />,
        key: `${item.id}-value`,
      },
      {
        content: getButton(index),
        key: `${item.id}-action`,
      },
    ],
  });

  const getRows = () => {
    return localVariables.map((variable, index) => createRow(variable, index));
  };

  const onAddFlowVariable = () => {
    const newVariable = { name: "", value: "", id: uuid() };
    const updatedVariables = [...localVariables, newVariable];
    setLocalVariables(updatedVariables);
    if (saved) {
      dispatch(setEditorSaved(false));
    }
  };

  return (
    <>
      <DynamicTable head={staticVariableHeaders} rows={getRows()} />
      <Button
        onClick={onAddFlowVariable}
        iconBefore={AddIcon}
        aria-label="Add Variable"
      />
      &nbsp;&nbsp;Add Variable
      <p></p>
      <Collapse defaultActiveKey={[]}>
        <Panel header="Post-Request Variables defined in actions" key="1">
          These variables will be put into the <b>_flow</b> variable together with the variables defined above. However, these variables are
          processed and defined after the action has executed.
          <p></p>
          <ul>{actionFlowVars}</ul>
        </Panel>
      </Collapse>
    </>
  );
};

export default SharedFlowVariablesTable;
